/**
 * Hook managing filtered sibling orders derived from the filtering orchestrator.
 * @module dataAccumulator/useFilteredSiblingOrders
 */
import { useCallback, useEffect, useState } from "react";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../data-model/medicalPaOrdersModel";
import {
  SiblingOrder,
  SiblingOrdersResponse,
} from "../../data-model/siblingOrdersModel";
import {
  FilterJCodeInput,
  getFilteredSiblingOrdersForParent,
  ParentOrderCoverageDetails,
} from "../utils/filterSiblingOrdersBasedOnConditions";
import { buildFilterJCodesFromAuthOnFile } from "../utils/siblingOrderFilter/buildFilterJCodesFromAuthOnFile";

/**
 * Inputs required to recompute filtered sibling orders on demand.
 */
interface UseFilteredSiblingOrdersParams {
  /** General details of the parent order, or undefined until fetched. */
  parentOrder: GeneralOrderDetails | undefined;
  /** Coverage details for the parent order, or undefined until fetched. */
  parentOrderCoverageDetails: ParentOrderCoverageDetails | null | undefined;
  /** Auth-on-file payload for the parent order, or undefined until fetched. */
  authOnFile: AuthOnFile | undefined;
  /** Raw sibling orders from the sibling-orders domain, or undefined until fetched. */
  siblings: SiblingOrder[] | undefined;
}

/**
 * Manages `filteredSiblingOrders` state and exposes a callback to recompute it
 * from live parent J-code inputs (e.g. derived from the CPT table).
 *
 * @param params - {@link UseFilteredSiblingOrdersParams}
 * @returns Filtered sibling orders state and the recompute callback
 */
export const useFilteredSiblingOrders = ({
  parentOrder,
  parentOrderCoverageDetails,
  authOnFile,
  siblings,
}: UseFilteredSiblingOrdersParams) => {
  const [filteredSiblingOrders, setFilteredSiblingOrders] =
    useState<SiblingOrdersResponse | null>(null);

  /**
   * Runs the sibling-order filtering orchestrator against the currently loaded
   * parent order and sibling orders using the supplied parent J-codes, then
   * stores the flattened result.
   *
   * @param parentJCodes - Parent J-codes limited to the fields the pipeline
   *   consumes (see {@link FilterJCodeInput})
   */
  const recomputeFilteredSiblingOrders = useCallback(
    (parentJCodes: FilterJCodeInput[]) => {
      if (!parentOrder || !siblings) {
        return;
      }

      const passedSiblings = getFilteredSiblingOrdersForParent(
        parentOrder,
        parentOrderCoverageDetails,
        parentJCodes,
        siblings,
      );

      setFilteredSiblingOrders({ siblings: passedSiblings });
    },
    [parentOrder, parentOrderCoverageDetails, siblings],
  );

  /**
   * Recomputes filtered siblings whenever the parent order, auth-on-file, or
   * raw sibling list changes so modals can show linked orders without visiting
   * the CPT page first. CPT table edits may still override via the explicit
   * {@link recomputeFilteredSiblingOrders} callback.
   */
  useEffect(() => {
    if (!parentOrder || !siblings) {
      setFilteredSiblingOrders(null);
      return;
    }

    const parentJCodes = buildFilterJCodesFromAuthOnFile(authOnFile);
    recomputeFilteredSiblingOrders(parentJCodes);
  }, [
    parentOrder,
    parentOrderCoverageDetails,
    authOnFile,
    siblings,
    recomputeFilteredSiblingOrders,
  ]);

  return { filteredSiblingOrders, recomputeFilteredSiblingOrders };
};
